import React, { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Scale, Eye, EyeOff, Mail, Lock, User } from 'lucide-react';
import { useAutenticacao } from '../contextos/useAutenticacao';

/**
 * PaginaCadastro
 *
 * CONTEXTO DE NEGÓCIO:
 * Página de cadastro para criar nova conta na aplicação. Deve seguir os
 * mesmos padrões visuais e de usabilidade da `PaginaLogin`.
 *
 * REQUISITOS IMPLEMENTADOS:
 * - Campos: nome, email, senha, confirmar senha, empresa (opcional), cargo (opcional)
 * - Validações básicas no frontend
 * - Indicador de força de senha
 * - Toggle para mostrar/ocultar senha
 * - Loading state no botão de submit
 */
export function PaginaCadastro(): JSX.Element {
  const navigate = useNavigate();
  const { cadastrar, estaCarregando, erro } = useAutenticacao();

  const [nomeCompleto, setNomeCompleto] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [senha, setSenha] = useState<string>('');
  const [confirmacaoSenha, setConfirmacaoSenha] = useState<string>('');
  const [empresa, setEmpresa] = useState<string>('');
  const [cargo, setCargo] = useState<string>('');
  const [mostrarSenha, setMostrarSenha] = useState<boolean>(false);
  const [erroLocal, setErroLocal] = useState<string | null>(null);

  /** Calcula força da senha de 0 a 100 baseado em critérios simples descritos na tarefa */
  function calcularForcaSenha(valor: string): number {
    let pontuacao = 0;
    if (valor.length >= 8) pontuacao += 25;
    if (/[A-Z]/.test(valor)) pontuacao += 25;
    if (/[a-z]/.test(valor)) pontuacao += 25;
    if (/[0-9]/.test(valor)) pontuacao += 25;
    return pontuacao;
  }

  function etiquetaForca(p: number): string {
    if (p >= 75) return 'Forte';
    if (p >= 50) return 'Média';
    return 'Fraca';
  }

  function corForca(p: number): string {
    if (p >= 75) return 'bg-green-400';
    if (p >= 50) return 'bg-yellow-400';
    return 'bg-red-400';
  }

  function validarFormulario(): string | null {
    if (!email) return 'Email é obrigatório.';
    const re = /^\S+@\S+\.\S+$/;
    if (!re.test(email)) return 'Formato de email inválido.';
    if (!nomeCompleto || nomeCompleto.trim().length < 2) return 'Nome completo deve ter ao menos 2 caracteres.';
    if (!senha) return 'Senha é obrigatória.';
    if (senha.length < 8) return 'A senha deve ter ao menos 8 caracteres.';
    if (!/[A-Z]/.test(senha)) return 'A senha deve conter ao menos 1 letra maiúscula.';
    if (!/[a-z]/.test(senha)) return 'A senha deve conter ao menos 1 letra minúscula.';
    if (!/[0-9]/.test(senha)) return 'A senha deve conter ao menos 1 número.';
    if (senha !== confirmacaoSenha) return 'Confirmação de senha não coincide.';
    return null;
  }

  async function aoSubmeter(event: FormEvent) {
    event.preventDefault();
    setErroLocal(null);
    const valido = validarFormulario();
    if (valido) {
      setErroLocal(valido);
      return;
    }

    try {
      // Repare: o contexto `cadastrar` deve aceitar um objeto com os campos necessários.
      await cadastrar({ nome: nomeCompleto, email, senha, empresa: empresa || undefined, cargo: cargo || undefined });
  // Após cadastro bem-sucedido, o contexto faz login automático; redirecionamos para a página inicial
  navigate('/');
    } catch (err) {
      setErroLocal((err as Error)?.message || 'Erro ao criar conta');
    }
  }

  function aoDigitar() {
    if (erroLocal) setErroLocal(null);
  }

  const forca = calcularForcaSenha(senha);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4 py-8">
      <div className="w-full max-w-2xl rounded-lg bg-white p-8 shadow-lg">
        <div className="mb-6 text-center">
          <Scale className="mx-auto mb-4 size-12 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Criar Conta</h1>
          <p className="mt-2 text-gray-600">Preencha os dados para começar</p>
        </div>

        {(erroLocal || erro) && (
          <div className="mb-4 flex items-start gap-2 rounded-md border border-red-100 bg-red-50 p-3">
            <Lock className="mt-0.5 size-5 text-red-600" aria-hidden />
            <div className="text-sm text-red-700">{erroLocal || erro}</div>
          </div>
        )}

        <form onSubmit={aoSubmeter} aria-label="Formulário de cadastro">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Nome completo</span>
              <div className="relative mt-1 rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <User className="size-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={nomeCompleto}
                  onChange={(e) => { setNomeCompleto(e.target.value); aoDigitar(); }}
                  required
                  aria-required
                  className="block w-full rounded-md border py-2 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-primary-600"
                  placeholder="Seu nome completo"
                />
              </div>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">Email</span>
              <div className="relative mt-1 rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Mail className="size-4 text-gray-400" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); aoDigitar(); }}
                  required
                  aria-required
                  className="block w-full rounded-md border py-2 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-primary-600"
                  placeholder="seu@exemplo.com"
                />
              </div>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">Senha</span>
              <div className="relative mt-1 rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Lock className="size-4 text-gray-400" />
                </div>
                <input
                  type={mostrarSenha ? 'text' : 'password'}
                  value={senha}
                  onChange={(e) => { setSenha(e.target.value); aoDigitar(); }}
                  required
                  aria-required
                  minLength={8}
                  className="block w-full rounded-md border px-10 py-2 focus:outline-none focus:ring-2 focus:ring-primary-600"
                  placeholder="********"
                />
                <button
                  type="button"
                  onClick={() => setMostrarSenha((s) => !s)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                  aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {mostrarSenha ? <EyeOff className="size-5 text-gray-500" /> : <Eye className="size-5 text-gray-500" />}
                </button>
              </div>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">Confirme a senha</span>
              <div className="relative mt-1 rounded-md shadow-sm">
                <input
                  type={mostrarSenha ? 'text' : 'password'}
                  value={confirmacaoSenha}
                  onChange={(e) => { setConfirmacaoSenha(e.target.value); aoDigitar(); }}
                  required
                  aria-required
                  minLength={8}
                  className="block w-full rounded-md border py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-primary-600"
                  placeholder="********"
                />
              </div>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">Empresa (opcional)</span>
              <div className="mt-1">
                <input
                  type="text"
                  value={empresa}
                  onChange={(e) => setEmpresa(e.target.value)}
                  className="block w-full rounded-md border py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-primary-600"
                  placeholder="Nome da empresa"
                />
              </div>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">Cargo (opcional)</span>
              <div className="mt-1">
                <input
                  type="text"
                  value={cargo}
                  onChange={(e) => setCargo(e.target.value)}
                  className="block w-full rounded-md border py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-primary-600"
                  placeholder="Seu cargo"
                />
              </div>
            </label>
          </div>

          {/* Indicador de força da senha */}
          <div className="mt-4">
            <div className="mb-1 flex items-center justify-between text-sm text-gray-600">
              <span>Força da senha</span>
              <span className="font-medium">{etiquetaForca(forca)}</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded bg-gray-200">
              <div className={`h-2 ${corForca(forca)}`} style={{ width: `${forca}%` }} />
            </div>
          </div>

          <div className="mt-6">
            <button
              type="submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2 font-medium text-white hover:bg-primary-700 disabled:opacity-60"
              disabled={estaCarregando}
            >
              {estaCarregando ? 'Criando conta...' : 'Criar Conta'}
            </button>
          </div>

          <p className="mt-4 text-center text-sm text-gray-600">
            Já tem conta? <Link to="/login" className="text-primary-600 underline">Faça login</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

export default PaginaCadastro;
